// PRD-57 — the SPA half of the event catalogue.
//
// `AnalyticsEventProperties` is the single source of truth for which events
// the browser may emit and which properties each one may carry. It is a
// TYPE on purpose: `analytics.track()` is generic over it, so an event name
// that is not listed, or a property that is not declared for that event,
// fails `tsc` — which means a student id cannot reach PostHog by accident,
// only by someone adding it to this file where a reviewer will see it.
//
// Adding an event: `.claude/rules/analytics-events.md` (or `/add-analytics-event`).
// The table in docs/prd/57-professor-product-analytics.md changes in the same PR.
//
// What may be a property (PRD-57 FR-6): ids of PROFESSOR-owned objects
// (classroom, material, task), enums, counts, durations, error codes.
// Never: student ids, assignment ids (an assignment is one student), names,
// emails, grades, notes. `topic` is the one free-text exception, truncated.

import type { MaterialOrigin } from "@/constants/materials"
import type { AdaptiveMode, EducationLevel, MaterialStatus, RegenerationReason } from "@/types"
import type { NoteDepth, NoteSectionKey } from "@/types/classNotes"
import type { LessonRegenerationKey } from "@/types/lessonPlans"

export const ANALYTICS_APP = "professor-web" as const

type NoProps = Record<string, never>

type GradeSource = "material_results" | "review_queue" | "photo_grading"

export interface AnalyticsEventProperties {
  // ── Session ────────────────────────────────────────────────────────
  professor_logged_in: NoProps
  // Fire BEFORE `analytics.reset()` — afterwards PostHog no longer knows who
  // it was and the event lands on an anonymous visitor.
  professor_logged_out: NoProps
  // The forced logout in `api/client.ts` (refresh token rejected). It
  // bypasses `AuthContext.logout()`, so without its own event a session
  // that died looks identical to one that is still open.
  professor_session_expired: NoProps

  // ── Navigation ─────────────────────────────────────────────────────
  page_viewed: { route: string }
  classroom_roster_opened: { classroom_id: string }

  // ── Onboarding / feedback ──────────────────────────────────────────
  onboarding_completed: { students_added: number }
  feedback_submitted: { kind: string; category: string; attachment_count: number }

  // ── Classrooms ─────────────────────────────────────────────────────
  classroom_created: { classroom_id: string }
  classroom_updated: { classroom_id: string }
  classroom_archived: { classroom_id: string }
  classroom_restored: { classroom_id: string }

  // ── Roster ─────────────────────────────────────────────────────────
  student_created: { classroom_id: string }
  // `source` says which surface saved: the roster modal or the person page.
  student_updated: { source: "roster" | "profile" }
  student_deleted: NoProps
  roster_imported: { classroom_id: string; created: number; skipped: number }
  survey_invite_sent: {
    mode: "single" | "bulk"
    classroom_id?: string
    dispatched?: number
    skipped?: number
  }
  student_note_saved: { action: "created" | "updated" | "deleted" }

  // ── Materials ──────────────────────────────────────────────────────
  // `..._requested`, not `material_created`: generation is asynchronous
  // (PRD-18) and the mutation only proves the backend ACCEPTED the job. The
  // outcome is `material_generation_settled`, emitted by the server.
  material_generation_requested: {
    material_id: string
    classroom_id: string
    material_type: string
    item_type: string
    question_count: number
    language: string | null
    education_level: string
    total_assignments: number
    topic: string
    // PRD-64 — how many sources were attached and how many as a style example.
    reference_count: number
    reference_example_count: number
  }
  material_approved: { material_id: string; scheduled: boolean }
  material_send_changed: {
    material_id: string
    action: "sent_now" | "rescheduled" | "schedule_cancelled"
  }
  material_cancelled: { material_id: string }
  material_deleted: { material_id: string }
  // PRD-62 — a sent material taken out of (and put back into) every grade.
  // PRD-69 — `material_origin` separates the professor's own materials from
  // the ones Platonica prepared and sent: on a staff material these are the
  // actions the professor CAN take, and genuine usage worth telling apart.
  // Not `origin`: PRD-66 gave that name another meaning server-side.
  material_archived: { material_id: string; classroom_id: string; material_origin: MaterialOrigin }
  material_restored: { material_id: string; classroom_id: string; material_origin: MaterialOrigin }
  material_generation_retried: { scope: "one" | "all_failed"; material_id?: string }
  // PRD-66 — copies generated for students who joined the group after the
  // material(s). Counts only: never which students.
  late_students_backfill_requested: {
    classroom_id: string
    source: "material_detail" | "roster_banner"
    materials: number
    copies: number
  }
  // PRD-63 — a new version of the same material. The comment never leaves:
  // only whether there was one and how long it was.
  material_regeneration_requested: {
    material_id: string
    classroom_id: string
    round: number
    reasons: RegenerationReason[]
    has_comment: boolean
    comment_length: number
    prior_status: MaterialStatus
    students: number
  }
  // PRD-65 — the professor edited one student's content. The assignment is
  // one student, so only the material id, a count and a flag travel.
  material_content_edit_saved: { material_id: string; edited_item_count: number; has_math: boolean }
  // `source` tells the drawer's "Generar PDF" from the compile the send
  // modal runs before approving.
  material_content_compile_requested: { material_id: string; source: "drawer" | "approve" }
  material_content_edit_discarded: { material_id: string }
  material_email_resent: { scope: "all" | "one" | "retry_failed"; material_id?: string }
  // PRD-73 — «Dar más tiempo» after the deadline. Counts only: which students
  // (and so which assignments) never travel. `without_email` counts the
  // reopened rows with no address on file; a double click the server
  // recognises (`already_open`) reopened nothing and emits nothing.
  material_submission_reopened: {
    material_id: string
    scope: "single" | "bulk"
    count: number
    grades_cleared: number
    without_email: number
  }
  // The professor copied the new link to send it herself (WhatsApp). Not a
  // mutation: emitted from the copy handler, and only when the copy worked.
  material_reopen_link_copied: { material_id: string }
  material_downloaded: { material_id: string; kind: "bulk_zip" }
  // PRD-61 §FR-5.1 — the professor moved a material to another evaluation
  // cut. `cut: null` = back to "by due date".
  material_cut_changed: { material_id: string; classroom_id: string; cut: number | null }

  // ── Class notes and books (PRD-70) ────────────────────────────────
  // Notes are shared documents, not per-student Material assignments.
  // Requested events prove API acceptance, never render/email completion.
  class_syllabus_saved: {
    classroom_id: string
    unit_count: number
    topic_count: number
    pending_count: number
    in_progress_count: number
    taught_count: number
  }
  class_note_generation_requested: {
    note_id: string
    education_level: EducationLevel
    depth: NoteDepth
    classroom_count: number
    topic_count: number
  }
  class_note_revision_requested:
    | { note_id: string; source: "manual" }
    | { note_id: string; source: "ai_section"; section_key: NoteSectionKey }
  class_note_candidate_reviewed: { note_id: string; action: "accepted" | "discarded" }
  class_note_published: {
    note_id: string
    revision_id: string
    classroom_count: number
    send_email: boolean
  }
  class_book_updated: {
    classroom_id: string
    updated_chapter_count: number
    removed_chapter_count: number
  }
  class_book_export_requested: { classroom_id: string; job_id: string }
  class_notes_job_retry_requested: { job_id: string }
  // `selected_count` describes the request, not queued/sent/delivered emails.
  // Delivery IDs identify recipients and must never leave the product.
  class_note_delivery_retry_requested: { note_id: string; selected_count: number; resend: boolean }

  // PRD-71 — teacher-private lesson plans. No document content or student identifiers.
  lesson_plan_generation_requested: {
    plan_id: string
    classroom_id: string
    education_level: EducationLevel
    duration_minutes: number
    topic_count: number
    include_history: boolean
  }
  lesson_plan_revision_requested:
    | { plan_id: string; source: "manual" }
    | { plan_id: string; source: "ai_section"; section_key: LessonRegenerationKey }
  lesson_plan_candidate_reviewed: { plan_id: string; action: "accepted" | "discarded" }
  lesson_plan_taught_confirmed: {
    plan_id: string
    classroom_id: string
    covered_count: number
    pending_count: number
  }
  lesson_plan_pdf_opened: { plan_id: string; revision_id: string }
  lesson_plan_job_retry_requested: { job_id: string }

  // ── Grading ────────────────────────────────────────────────────────
  // Same business fact from three flows → one event, `source` tells them
  // apart. Three separate events would make the total something somebody
  // has to remember to add up.
  grade_saved: { source: GradeSource; accept_path?: string; task_id?: string }
  grading_upload_created: { image_count: number; regrade: boolean }
  grading_upload_deleted: NoProps

  // ── Reference sources (PRD-64) ────────────────────────────────────
  // Counts, enums and booleans only — never the title, filename or text.
  reference_created: {
    kind: "file" | "image" | "text" | "url"
    bytes_bucket: "lt_1mb" | "1_5mb" | "gt_5mb"
    has_page_range: boolean
    has_subject: boolean
  }
  reference_deleted: { kind: "file" | "image" | "text" | "url"; used_in_materials: number }
  review_queue_completed: { material_id: string; approved: number; skipped: number }

  // ── Everything else ────────────────────────────────────────────────
  notification_opened: NoProps
  settings_updated: { fields: string }
  password_changed: NoProps
  // One event for the six adaptive-level actions, told apart by `action`.
  // `mode_set` carries the mode chosen, so a dashboard can tell a pause from
  // an opt-in. Never the student id nor the free-text teacher reason.
  // (`score_recorded` retired with the adaptive exercise score, PRD-48.)
  adaptive_level_changed:
    | { action: "mode_set"; classroom_id: string; mode: AdaptiveMode }
    | {
        action: "accepted" | "rejected" | "overridden" | "pin_changed" | "undone"
        classroom_id: string
      }
  // `grades_csv` is the PRD-61 kardex; its `rows` is the classroom's student
  // count (one row per active student), not a parse of the file.
  export_downloaded: { kind: "roster_csv" | "grades_csv"; rows: number }
}

export type AnalyticsEvent = keyof AnalyticsEventProperties

/**
 * Student-facing surfaces served by this same SPA. NOTHING is captured on
 * them — not events, not errors, not exceptions, not failed requests. The
 * gate lives once, inside `lib/analytics.ts`, so a new capture path cannot
 * forget it (`.claude/rules/guard-at-the-cause.md` §2).
 */
const STUDENT_ROUTE_PREFIXES = ["/encuesta", "/entregar"] as const

/**
 * Public surfaces, mirrored from the routes declared OUTSIDE
 * `<ProtectedRoute>` in `App.tsx`: the student surfaces above plus the
 * unauthenticated professor flows. These get no `page_viewed` (there is no
 * professor to attribute it to), but errors on `/login` ARE still captured.
 *
 * Deliberately a deny-list rather than an allow-list of professor routes.
 * Both are mirrors of the router and both can drift, but they drift in
 * opposite directions: a forgotten professor route in an allow-list is
 * silently untracked and nobody ever finds out, while a forgotten public
 * route here shows up in PostHog as a route nobody recognises. Between two
 * equally fragile designs, prefer the one that fails loudly.
 */
const PUBLIC_ROUTE_PREFIXES = [
  ...STUDENT_ROUTE_PREFIXES,
  "/login",
  "/activar",
  "/restablecer",
] as const

const matchesPrefix = (pathname: string, prefixes: readonly string[]): boolean =>
  prefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))

export const isStudentRoute = (pathname: string): boolean =>
  matchesPrefix(pathname, STUDENT_ROUTE_PREFIXES)

export const isProfessorRoute = (pathname: string): boolean =>
  !matchesPrefix(pathname, PUBLIC_ROUTE_PREFIXES)

/**
 * Every route parameter in `App.tsx` (`classroomId`, `studentId`,
 * `profileId`, `materialId`) is a database UUID, so a raw pathname carries
 * identifiers — including student ones — straight into a third party, and
 * blows up the cardinality of any `route` breakdown.
 *
 * Collapse them to `:id` so `route` answers "which screen". Also used for
 * API URLs (`/students/<uuid>` → `/students/:id`), where the same two
 * problems apply. When a concrete id is genuinely needed it is attached as
 * its own declared property on a specific event, never smuggled inside a
 * path string.
 *
 * `useMatches()` would be the tidier source for routes, but it only works
 * under a data router; this app mounts `<BrowserRouter>` + `<Routes>`.
 */
const UUID_SEGMENT = /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\/|$)/gi

// The public token routes carry a credential, not a UUID, in the path. Until
// 2026-10 `/activar/<token>` and `/restablecer/<token>` reached PostHog whole
// on every error those pages logged.
const TOKEN_SEGMENT = /^\/(activar|restablecer|entregar)\/[^/]+/

export const toRoutePattern = (pathname: string): string =>
  pathname.replace(TOKEN_SEGMENT, "/$1/:token").replace(UUID_SEGMENT, "/:id")

/** The classroom id when the pathname is that classroom's roster, else null. */
export const classroomRosterId = (pathname: string): string | null =>
  /^\/salones\/([^/]+)\/alumnos$/.exec(pathname)?.[1] ?? null

// PRD-57 FR-6 — `topic` is the one free-text property allowed out, because
// it is curricular content rather than personal data. Truncated so a
// professor who pastes a paragraph does not ship a paragraph.
export const ANALYTICS_TOPIC_MAX_LENGTH = 120

export const truncateTopic = (topic: string): string =>
  topic.trim().slice(0, ANALYTICS_TOPIC_MAX_LENGTH)
